import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NextCta } from '../Layout';
import { FadeLink, Section, cx, reducedMotion, useCopy } from '../core';
import agenticIllustration from '../assets/img/solution-agentic.webp';
import integrationsIllustration from '../assets/img/solution-integrations.webp';
import trustworthyIllustration from '../assets/img/solution-trustworthy.webp';

const copy = {
  en: {
    pill: 'Custom AI services', title: <>Tailored AI solutions<br />for organizations.</>,
    intro: 'Bring AI research and engineering to your business problems, existing systems and data environment.',
    discuss: 'Discuss your project', how: 'See how we work',
    svcLabel: 'Services', svcTitle: <>From a problem to<br />a working solution.</>,
    svcText: 'Three starting points for a conversation. Requirements, boundaries and acceptance are agreed with your team before anything is built.',
    scope: 'ENQUIRY SCOPE',
    services: [
      ['Enterprise Agentic AI', 'Discuss how AI agents could fit a specific workflow and its permission boundaries.', 'Requirements, human review and implementation boundaries.', 'Concept illustration: an agent task with three steps, the last one waiting for human review, and read / draft / no-send permissions'],
      ['Custom Integrations', 'Explore integration needs around existing systems and operating environments.', 'Interfaces, support conditions and delivery boundaries.', 'Concept illustration: one integration hub connected to API, ERP, CRM, database, files and identity'],
      ['Trustworthy AI', 'Discuss data boundaries, privacy preservation and autonomous-agent research directions.', 'Scenarios, evaluation and acceptance criteria to be agreed.', 'Concept illustration: private data kept inside an agreed scope, checked against scenarios, criteria and acceptance']
    ],
    foot: 'Concept illustrations. Final scope, interfaces and evaluation are agreed per project.',
    procLabel: 'Process', procTitle: <>An illustrative<br />collaboration process.</>,
    procText: 'A typical path from first conversation to launch. Phases, deliverables and timing are agreed for each project.',
    phasesLabel: 'Collaboration phases', phase: 'PHASE',
    tags: ['UNDERSTAND · DEFINE', 'SCOPE · AGREE', 'VALIDATE · ITERATE', 'LAUNCH · MAINTAIN'],
    phases: [
      ['Discovery', 'Understand users, the business problem and success criteria.', ['Users', 'Business problem', 'Success criteria']],
      ['Proposal', 'Agree on scope, deliverables and evaluation.', ['Scope', 'Deliverables', 'Evaluation']],
      ['Build', 'Validate a prototype in real scenarios and iterate.', ['Prototype', 'Real scenarios', 'Iteration']],
      ['Deployment', 'Plan launch, onboarding and maintenance.', ['Launch', 'Onboarding', 'Maintenance']]
    ]
  },
  zh: {
    pill: '定制 AI 服务', title: <>为机构需求<br />构建 AI 方案。</>,
    intro: '将 AI 研究与工程能力，应用到您的业务问题、现有系统和数据环境中。',
    discuss: '讨论您的项目', how: '了解合作方式',
    svcLabel: '服务', svcTitle: <>从问题出发，<br />到可用的方案。</>,
    svcText: '三个对话起点。在动手构建之前，需求、边界与验收标准都会先与您的团队达成一致。',
    scope: '咨询范围',
    services: [
      ['企业智能体咨询', '围绕一个实际工作流，讨论 AI 智能体的适用方向与权限边界。', '需求、人工审核节点与实施范围。', '概念示意：一个包含三个步骤的智能体任务，最后一步等待人工审核，并标注只读、起草、不发送等权限'],
      ['定制系统集成', '围绕现有系统与使用环境，讨论可能的集成需求。', '接口、支持条件与交付边界。', '概念示意：一个集成中枢连接 API、ERP、CRM、数据库、文件与身份系统'],
      ['可信机制咨询', '围绕数据范围、隐私保护和自主智能体研究方向开展交流。', '场景、评估方法与待确认的验收标准。', '概念示意：私有数据保持在约定范围内，并按场景、标准与验收进行核对']
    ],
    foot: '以上为概念示意。最终范围、接口与评估方式按项目约定。',
    procLabel: '流程', procTitle: <>合作流程<br />示意。</>,
    procText: '从首次沟通到上线的典型路径。各阶段、交付物与时间安排按项目约定。',
    phasesLabel: '合作阶段', phase: '阶段',
    tags: ['理解 · 定义', '范围 · 确认', '验证 · 迭代', '上线 · 维护'],
    phases: [
      ['需求沟通', '明确使用者、业务问题与成功标准。', ['使用者', '业务问题', '成功标准']],
      ['方案设计', '确认范围、交付物与评估方式。', ['范围', '交付物', '评估方式']],
      ['开发验证', '以真实场景验证原型，逐步完善。', ['原型', '真实场景', '迭代']],
      ['部署交付', '规划上线、使用培训与维护安排。', ['上线', '使用培训', '维护']]
    ]
  }
};

const ILLUSTRATIONS = [agenticIllustration, integrationsIllustration, trustworthyIllustration];

const TAG_ICONS = [
  <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  <><path d="M9 3h6v4H9z" /><path d="M15 5h3v16H6V5h3" /><path d="m9 14 2 2 4-4" /></>,
  <><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><path d="M21 3v5h-5" /></>,
  <><path d="M5 15c-1.5 1.5-2 5-2 5s3.5-.5 5-2" /><path d="M9 15l-3-3c2-5 6-9 13-9 0 7-4 11-9 13z" /><circle cx="15" cy="9" r="1.5" /></>
];
const PHASE_ICONS = [
  <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
  <><path d="M21 8 12 3 3 8l9 5 9-5z" /><path d="M3 8v8l9 5 9-5V8M12 13v8" /></>,
  <path d="M7 17 17 7M8 7h9v9" />
];

// Plays 01→04 once when the timeline comes into view; hover, focus or tap makes a phase current.
function Process({ t }) {
  const timeline = useRef(null);
  const rail = useRef(null);
  const cards = useRef([]);
  const timer = useRef(null);
  const auto = useRef(false);
  const [current, setCurrent] = useState(-1);
  const [offsets, setOffsets] = useState([]);
  const layout = useCallback(() => {
    if (!rail.current) return;
    const origin = rail.current.getBoundingClientRect().left;
    setOffsets(cards.current.map((card) => (card ? card.getBoundingClientRect().left - origin : 0)));
  }, []);
  useLayoutEffect(() => {
    layout();
    window.addEventListener('resize', layout);
    return () => window.removeEventListener('resize', layout);
  }, [layout]);
  const stop = () => { auto.current = false; window.clearTimeout(timer.current); };
  useEffect(() => {
    if (reducedMotion()) { setCurrent(0); return undefined; }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      auto.current = true;
      let index = 0;
      const step = () => {
        if (!auto.current) return;
        setCurrent(index);
        index += 1;
        if (index < 4) timer.current = window.setTimeout(step, 1500); else auto.current = false;
      };
      timer.current = window.setTimeout(step, 300);
    }, { threshold: 0.45 });
    observer.observe(timeline.current);
    return () => { observer.disconnect(); stop(); };
  }, []);
  const choose = (index) => { stop(); setCurrent(index); };
  const centers = offsets.map((x) => x + 14);
  const end = current === 3;
  const flowWidth = current >= 0 && centers.length ? Math.max(0, centers[current] - centers[0] + (end ? 0 : 120)) : 0;
  return <Section className="proc" id="process">
    <div className="wrap">
      <div className="shead">
        <div data-reveal="" style={{ '--i': 0 }}><p className="lbl">{t.procLabel}</p><h2>{t.procTitle}</h2></div>
        <div className="r" data-reveal="" style={{ '--i': 1 }}><p>{t.procText}</p></div>
      </div>
      <div className="tl" ref={timeline} data-reveal="" style={{ '--i': 2 }}>
        <div className="rail" ref={rail} aria-hidden="true"><div className="base" /><div className={cx('rflow', end && 'end')} style={{ width: flowWidth }} />
          {t.tags.map((_, index) => <span key={index} className={cx('node', index === current && 'on', index < current && 'reached')} style={{ left: offsets[index] || 0 }}>{`0${index + 1}`}</span>)}
          {t.tags.map((tag, index) => <span key={tag} className={cx('itag', index === current && 'on')} style={{ left: (offsets[index] || 0) + 44 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{TAG_ICONS[index]}</svg>{tag}</span>)}
        </div>
        <ol className="phases" aria-label={t.phasesLabel}>
          {t.phases.map(([title, text, effects], index) => (
            <li key={index} ref={(node) => { cards.current[index] = node; }} className={cx('phase', index === current && 'on')} tabIndex={0}
              onMouseEnter={() => choose(index)} onFocus={() => choose(index)} onClick={() => choose(index)}>
              <div className="top"><span className="ic"><svg viewBox="0 0 24 24" aria-hidden="true">{PHASE_ICONS[index]}</svg></span><span className="ph">{`${t.phase} 0${index + 1}`}</span></div>
              <h3>{title}</h3><p>{text}</p>
              <div className="fx">{effects.map((effect) => <span key={effect}>{effect}</span>)}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  </Section>;
}

export default function SolutionsPage() {
  const { t } = useCopy(copy);
  return <>
    <section className="lhero shero" aria-labelledby="h1">
      <div className="grid" aria-hidden="true" />
      <div className="glow" aria-hidden="true" />
      <div className="wrap copy hin">
        <span className="apill" style={{ '--i': 0 }}>{t.pill}</span>
        <h1 id="h1" style={{ '--i': 1 }}>{t.title}</h1>
        <p className="intro" style={{ '--i': 2 }}>{t.intro}</p>
        <div className="ctas" style={{ '--i': 3 }}>
          <FadeLink className="btn btn-dark" to="/contact?type=custom">{t.discuss} <span className="ar" aria-hidden="true">↗</span></FadeLink>
          <a className="btn btn-wl" href="#process">{t.how}</a>
        </div>
      </div>
      <div className="bfade" aria-hidden="true" />
    </section>
    <Section className="svc" id="services">
      <div className="wrap">
        <div className="shead">
          <div data-reveal="" style={{ '--i': 0 }}><p className="lbl">{t.svcLabel}</p><h2>{t.svcTitle}</h2></div>
          <div className="r" data-reveal="" style={{ '--i': 1 }}><p>{t.svcText}</p>
            <FadeLink className="link" to="/contact?type=custom">{t.discuss} <span className="ar" aria-hidden="true">↗</span></FadeLink></div>
        </div>
        <div className="stiles">
          {t.services.map(([title, text, scope, alt], index) => (
            <article key={index} className="stile" data-reveal="" style={{ '--i': index + 1 }}>
              <img className="ill" src={ILLUSTRATIONS[index]} alt={alt} loading="lazy" />
              <p className="n">{`0${index + 1}`}</p><h3>{title}</h3>
              <p>{text}</p>
              <div className="scope"><small>{t.scope}</small><span>{scope}</span></div>
            </article>
          ))}
        </div>
        <p className="foot" data-reveal="">{t.foot}</p>
      </div>
    </Section>
    <Process t={t} />
    <NextCta />
  </>;
}
